import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import '../../styles/auth.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [username, setUsername] = useState('');
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const { newPassword, confirmPassword } = passwords;

  useEffect(() => {
    // Verify token on component mount
    const verifyToken = async () => {
      try {
        const res = await api.get(`/api/auth/verify-reset-token/${token}`);
        
        if (res.data.valid) {
          setTokenValid(true);
          setUsername(res.data.username);
        } else {
          setTokenValid(false);
          toast.error('Invalid or expired reset link');
        }
      } catch (err) {
        setTokenValid(false);
        const message = err.response?.data?.message || 'Invalid or expired reset link';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setLoading(false);
      setTokenValid(false);
    }
  }, [token]);

  const onChange = e => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const res = await api.post('/api/auth/reset-password', {
        token,
        newPassword
      });

      toast.success(res.data.message);
      
      // Redirect to login after successful reset
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const message = err.response?.data?.message || 'An error occurred. Please try again.';
      toast.error(message);
      
      // If token is invalid/expired, update the state
      if (err.response?.status === 400) {
        setTokenValid(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page page-narrow">
        <div className="auth-card">
          <div className="form-container">
            <h1>Verifying Reset Link...</h1>
            <p>Please wait while we verify your reset link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-page page-narrow">
        <div className="auth-card">
          <div className="form-container">
            <h1>Invalid Reset Link</h1>
            <div className="auth-error-message">
              <p>This password reset link is invalid or has expired.</p>
              <p>Password reset links are only valid for 15 minutes for security reasons.</p>
              <p>Please request a new password reset link.</p>
            </div>
            <div className="auth-links">
              <p><Link to="/forgot-password">Request New Reset Link</Link></p>
              <p><Link to="/login">Back to Login</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="form-container">
          <h1>Reset Password</h1>
          <p className="auth-description">
            Enter your new password for <strong>{username}</strong>
          </p>
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                name="newPassword"
                className="form-control"
                value={newPassword}
                onChange={onChange}
                placeholder="Enter new password"
                minLength="6"
                required
                disabled={submitting}
              />
              <small className="form-text">Password must be at least 6 characters</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                className="form-control"
                value={confirmPassword}
                onChange={onChange}
                placeholder="Confirm new password"
                required
                disabled={submitting}
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
          <div className="auth-links">
            <p><Link to="/login">Back to Login</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;